import clsx from 'clsx';
import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import styles from './AggregateLineOn.module.css';
import {
  AggregateStatusIndicator,
  VcStatusIndicator,
  PartAvailabilityIndicator,
} from '..';
import {
  loadAsnDates,
  splitDescription,
  compareByDateAndSerialNumber,
  formatDataToIncludeColour,
  getAggregateStatusAndModifiedAtAndComment,
} from '../../utils/tableUtils';
import { LinearProgress } from '@material-ui/core';
import {
  AGGREGATE_STATUS,
  API_RESOURCE_URLS,
  ASN_STATUS,
  DISPLAY_MESSAGES,
  MESSAGE_TYPE,
  USER_OPERATIONS,
  RESOURCE_TYPE,
  DATE_FORMAT,
  AGGREGATES,
  VC_ASN_STATUS,
} from '../../constants';
import { statusStringMap } from '../../staticData';
import { API } from '../../apis/api';
import { useAjaxCallbackWithIntervals } from '../../hooks';
import AggregateToolbar from '../AggregateToolbar/AggregateToolbar';
import {
  buildErrorMessage,
  getLastAsnGeneratedDateForPlantAndLine,
} from '../../apis/calls';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { withAllowedOperationsProvider } from '../../hocs';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import Table from '../Table/Table';
import { InfoIcon } from '../../icons';
import { optionalColumnsWithShift } from '../../staticData';
import { downloadCsv } from '../../utils/utils';

const AggregateLineOn = ({
  aggregate: materialGroupCluster,
  hasActiveEdits,
  setHasActiveEdits,
}) => {
  const ROW_HEIGHT = 38;

  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;

  const latestLineValueRef = useRef(line.value);
  const latestPlantValueRef = useRef(plant.value);

  const { showInternalError, showPopup } = usePopupManager();

  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });
  const [selectedOptionalColNames, setSelectedOptionalColNames] = useState([]);
  const [asnDates, setAsnDates] = useState([]);
  const [activeAsnDate, setActiveAsnDate] = useState(null);

  const dispatchRows = async rows => {
    try {
      const updates = rows.map(({ asnId, materialStatus }) => ({
        asnId,
        status: AGGREGATE_STATUS.LINE_ON,
        availability: materialStatus === AGGREGATE_STATUS.LINE_ON,
      }));

      await API.patch(
        API_RESOURCE_URLS.asnMaterialGroupClusters(
          plant.value,
          line.value,
          materialGroupCluster,
        ),
        updates,
      );
      loadLinedOutAggregateAsnForDate(materialGroupCluster, activeAsnDate); //reload data
      showPopup({
        type: MESSAGE_TYPE.SUCCESS,
        contextText: DISPLAY_MESSAGES.AGGREGATE_STATUS_UPDATED,
        info: '',
      });
    } catch (err) {
      console.error(buildErrorMessage(err));
      showPopup({
        type: MESSAGE_TYPE.FAILURE,
        error: err,
        contextText: DISPLAY_MESSAGES.VC_UPDATE_FAILED,
      });
    }
  };

  const aggregateColumns = [
    {
      width: 150,
      title: 'ASN',
      field: 'asn',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 150,
      title: 'Part Availability',
      field: 'materialNumber',
      alignment:
        materialGroupCluster !== AGGREGATES.MATERIAL ? 'left' : 'center',
      style: {
        boxSizing: 'content-box',
      },
      enableSearch: true,
      enableFilter: true,
      render: row => (
        <>
          {materialGroupCluster !== AGGREGATES.MATERIAL && (
            <div
              style={{
                display: 'inline-block',
                verticalAlign: 'top',
                minWidth: '97px',
              }}
            >
              {row.materialNumber}
            </div>
          )}
          <PartAvailabilityIndicator
            style={{ height: 17, width: 17, marginLeft: '6px' }}
            availability={row.materialAvailability}
            partNumber={row.materialNumber}
          />
        </>
      ),
    },
    materialGroupCluster !== AGGREGATES.MATERIAL
      ? {
          width: 210,
          title: 'Part Description',
          field: 'materialDescription',
          enableSearch: true,
          enableFilter: true,
        }
      : {
          width: 150,
          title: 'VC',
          field: 'vc',
          enableSearch: true,
          enableFilter: true,
        },
  ].filter(c => c);

  const vcColumns = [
    {
      width: 180,
      title: 'VC Description',
      field: 'vcDescription',
      enableSearch: true,
      enableFilter: true,
      render: (
        row,
        _,
        { openEllipsisPopupForEventWithText, closeEllipsisPopup, onCopy },
      ) => {
        const [descriptionHead, descriptionTail] = splitDescription(
          row.vcDescription,
        );
        return (
          <div
            className={row.vcStatus === VC_ASN_STATUS.LINE_OUT && styles.redRow}
            onMouseEnter={e =>
              openEllipsisPopupForEventWithText(e, row.vcDescription)
            }
            onMouseLeave={closeEllipsisPopup}
            onDoubleClick={() => onCopy(row.vcDescription)}
            style={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <span className={styles.boldBlue}>{descriptionHead}</span>{' '}
            {descriptionTail}
          </div>
        );
      },
      searchFn: (fieldValue, searchValue) =>
        fieldValue.toLowerCase().includes(searchValue.toLowerCase()),
    },
    {
      width: 120,
      title: 'VC Status',
      field: 'status',
      enableSearch: true,
      enableFilter: true,
      customFilterOptions: [
        {
          value: 'red',
          node: <InfoIcon style={{ color: '#F17C7C', fontSize: '1em' }} />,
        },
        {
          value: 'green',
          node: <InfoIcon style={{ color: '#49a47e', fontSize: '1em' }} />,
        },
        {
          value: 'grey',
          node: <InfoIcon style={{ color: '#7070708c', fontSize: '1em' }} />,
        },
      ],
      filterFn: (row, option) => {
        if (option === 'red')
          return (
            row.vcStatus === VC_ASN_STATUS.LINE_OUT ||
            row.hasUnAvailableAggregates
          );
        if (option === 'green')
          return (
            row.vcStatus !== VC_ASN_STATUS.LINE_OUT &&
            row.materialGroupClusters
              .map(group => ({
                name: group.name,
                ...group.materials[0],
              }))
              .every(material => material.availability)
          );
        if (option === 'grey')
          return (
            row.vcStatus !== VC_ASN_STATUS.LINE_OUT &&
            !row.hasUnAvailableAggregates &&
            row.materialGroupClusters
              .map(group => ({
                name: group.name,
                ...group.materials[0],
              }))
              .some(material => !material.availability)
          );
      },
      cellStyles: { height: '84%' },
      render: row => {
        if (row.vcStatus === VC_ASN_STATUS.LINE_OUT)
          return (
            <VcStatusIndicator
              rowData={row}
              fontSize="17px"
              style={{ marginLeft: 'auto', marginRight: 'auto' }}
            />
          );

        return (
          <AggregateStatusIndicator
            rowData={row}
            currentAggregate={materialGroupCluster}
            fontSize="17px"
            style={{ marginLeft: 'auto', marginRight: 'auto' }}
          />
        );
      },
    },
    {
      width: 150,
      title: 'Lined-Out on',
      field: 'modifiedAt',
      renderHeader: () => (
        <span style={{ paddingLeft: '2px' }}>Lined-Out on</span>
      ),
      render: row => (
        <span style={{ textAlign: 'left' }}>
          {moment(row.modifiedAt).format('DD MMMM YYYY')}
        </span>
      ),
    },
  ].map(c => ({ ...c }));

  const actionColumns = [
    {
      width: 150,
      title:
        materialGroupCluster === AGGREGATES.MATERIAL
          ? 'Material Status'
          : 'Aggregate Status',
      field: 'materialStatus',
      enableSearch: true,
      enableFilter: true,
      style: { textAlign: 'center', boxSizing: 'content-box' },
      render: row => {
        if (row.materialStatus === statusStringMap[AGGREGATE_STATUS.PLANNED])
          return <span style={{ opacity: 0.5 }}>{row.materialStatus}</span>;
        return row.materialStatus;
      },
    },
  ].filter(c => c);

  const fetchAndBuildAggregatesRowsData = async (materialGroupName, date) => {
    const dateString = date.format(DATE_FORMAT.ISO);
    const asnResponse = await API.get(API_RESOURCE_URLS.VC_ASN, {
      params: {
        from: dateString,
        to: dateString,
        line: line.value,
        plant: plant.value,
        status: [
          ASN_STATUS.READY_FOR_DROP,
          ASN_STATUS.DROPPED,
          ASN_STATUS.PLANNED,
          ASN_STATUS.LINE_OUT,
        ],
      },
    });

    const processedLinedOutAggregateAsns = processVcAsnResponseForMaterialGroup(
      asnResponse,
      materialGroupName,
    );
    const data = formatDataToIncludeColour(processedLinedOutAggregateAsns);
    return data;
  };

  const fetchAndBuildPreviousPendingLineOutAggregatesRowsData = async materialGroupName => {
    let lastAsnGeneratedDate;
    if (!plant.value || !line.value) return;
    lastAsnGeneratedDate = await getLastAsnGeneratedDateForPlantAndLine(
      plant.value,
      line.value,
    );
    const dayAfterLastAsnGeneratedDate = lastAsnGeneratedDate.add(1, 'days');
    const dateString = dayAfterLastAsnGeneratedDate.format(DATE_FORMAT.ISO);

    const asnResponse = await API.get(API_RESOURCE_URLS.VC_ASN, {
      params: {
        before: dateString,
        line: line.value,
        plant: plant.value,
        status: [
          ASN_STATUS.READY_FOR_DROP,
          ASN_STATUS.DROPPED,
          ASN_STATUS.PLANNED,
        ],
      },
    });

    const processedLinedOutAggregateAsns = processVcAsnResponseForMaterialGroup(
      asnResponse,
      materialGroupName,
    );
    const data = formatDataToIncludeColour(processedLinedOutAggregateAsns);
    return data;
  };

  const getLinedOutAggregatesDataBasedOnDate = (aggregate, date) => {
    const today = moment();
    if (date.isBefore(today, 'day'))
      return fetchAndBuildPreviousPendingLineOutAggregatesRowsData(aggregate);
    return fetchAndBuildAggregatesRowsData(aggregate, date);
  };

  const loadLinedOutAggregateAsnForDate = async (aggregate, date) => {
    if (!date)
      return setRowsData({
        loading: false,
        data: [],
        filteredData: [],
      });
    setRowsData(prevState => ({ ...prevState, loading: true }));
    try {
      const data = await getLinedOutAggregatesDataBasedOnDate(aggregate, date);

      if (
        plant.value !== latestPlantValueRef.current ||
        line.value !== latestLineValueRef.current
      )
        return setRowsData(prevState => ({ ...prevState, loading: false }));

      data.forEach(
        row => (row.materialStatus = statusStringMap[row.materialStatus]),
      );

      setRowsData({
        loading: false,
        data,
        filteredData: [...data],
      });
    } catch (err) {
      console.error(buildErrorMessage(err));
      showInternalError();
      setRowsData(prevState => ({ ...prevState, loading: false }));
    }
  };

  const prepareJsonHeaderAndValueFormats = aggregatePartNames => {
    const vcAggregateHeaderAndValueFormats = aggregatePartNames.map(
      partName => ({
        label: partName + ' Status',
        value: row =>
          row.materialGroupClusters.filter(group => group.name === partName)[0]
            ?.materials[0]?.aggregateStatus || 'unavailable',
      }),
    );

    const vcHeaderAndValueFormats = [
      {
        label: 'ASN',
        value: row => row.asn,
      },
      {
        label: 'Part Availability',
        value: row => '="' + row.materialNumber + '"',
      },
      {
        label: 'Part Description',
        value: row => row.materialDescription,
      },
      {
        label: 'VC Description',
        value: row => row.vcDescription,
      },
      {
        label: 'VC Status',
        value: row => row.vcStatus,
      },
      {
        label: 'Lined-Out On',
        value: row => moment(row.modifiedAt).format('DD MMMM YYYY'),
      },
      {
        label: 'Aggregate Status',
        value: row => row.materialStatus,
      },
    ].filter(c => c);

    return [...vcHeaderAndValueFormats, ...vcAggregateHeaderAndValueFormats];
  };

  const generateAndDownloadLineoutCsvReport = filteredData => {
    try {
      if (!rowsData.loading) {
        const dateValue = moment().format(DATE_FORMAT.ISO);

        const jsonData = filteredData;

        const aggregatePartNames = [];

        const { parseAsync } = require('json2csv');
        const fields = prepareJsonHeaderAndValueFormats(aggregatePartNames);
        const opts = { fields };
        parseAsync(jsonData, opts)
          .then(csv => {
            const exportedFilename =
              'AsnLineOutReport_' +
              plant.label +
              '_' +
              line.label +
              '_' +
              dateValue +
              '.csv';
            downloadCsv(csv, exportedFilename);
          })
          .catch(error =>
            console.error(
              'Failed to download Asn LineOut report, error:' +
                buildErrorMessage(error),
            ),
          );
      }
    } catch (error) {
      console.error(
        'Failed to download Asn Lineout report, error:' + buildErrorMessage(error),
      );
    }
  };

  useEffect(() => {
    latestLineValueRef.current = line.value;
    latestPlantValueRef.current = plant.value;

    setActiveAsnDate(null);
    loadAsnDates(
      plant.value,
      line.value,
      setAsnDates,
      setActiveAsnDate,
      null,
      latestPlantValueRef,
      latestLineValueRef,
    ).catch(err => console.error(buildErrorMessage(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant.value, line.value]);

  const lastRefreshedDate = useAjaxCallbackWithIntervals(
    rowsData.loading,
    hasActiveEdits,
    loadLinedOutAggregateAsnForDate,
    materialGroupCluster,
    activeAsnDate,
    plant.value,
    line.value,
  );

  return (
    <>
      <AggregateToolbar
        activeDate={activeAsnDate}
        dates={asnDates}
        handleDateSelect={date => setActiveAsnDate(date)}
        selectedOptionalColNames={selectedOptionalColNames}
        applyColumnsSelection={selectedColName =>
          setSelectedOptionalColNames(selectedColName)
        }
        lastRefreshedDate={lastRefreshedDate}
        contextualButtonProps={{
          className: clsx(
            moment().subtract(1, 'days').isSame(activeAsnDate, 'day')
              ? styles.greenRoundButton
              : styles.whiteRoundButton,
          ),
          text: 'All',
          label: 'View',
          handleClick: () => setActiveAsnDate(moment().subtract(1, 'days')),
        }}
        restrictNavigationDuringEditing
        hasActiveEdits={hasActiveEdits}
      />

      <div className={styles.container}>
        <div className={styles.tableView} style={{ height: '100%' }}>
          <Table
            columns={[
              ...aggregateColumns,
              ...vcColumns.filter(column => {
                const yesterday = moment().subtract(1, 'days');
                if (!yesterday.isSame(activeAsnDate, 'day'))
                  return column.title !== 'Lined-Out on';
                return true;
              }),
              ...optionalColumnsWithShift.map(c => ({
                ...c,
                enableSearch: true,
                enableFilter: true,
              })),
              ...actionColumns,
            ]}
            defaultColumns={[
              ...aggregateColumns,
              ...vcColumns.filter(column => {
                const yesterday = moment().subtract(1, 'days');
                if (!yesterday.isSame(activeAsnDate, 'day'))
                  return column.title !== 'Lined-Out on';
                return true;
              }),
              ...actionColumns,
            ]}
            rowHeight={ROW_HEIGHT}
            rows={rowsData.data}
            primaryAction={{
              name: 'Dispatch',
              actionFn: dispatchRows,
              authOperation: USER_OPERATIONS.UPDATE_AGGREGATE_DISPATCH,
              shouldEnable: selectedRows =>
                selectedRows.length &&
                selectedRows.every(
                  row =>
                    row.materialStatus ===
                    statusStringMap[AGGREGATE_STATUS.LINE_OUT],
                ),
            }}
            secondaryActions={[
              {
                name: 'DOWNLOAD CSV',
                getFilteredData: true,
                authOperation: USER_OPERATIONS.GET_CHASSIS_PLAN_ASN,
                shouldEnable: () => rowsData.data.length > 0,
                actionFn: filteredData =>
                  generateAndDownloadLineoutCsvReport(filteredData),
              },
            ]}
            setHasActiveEdits={setHasActiveEdits}
            setAsnCount={() => {}}
          />

          {rowsData.loading && (
            <div>
              <LinearProgress />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

AggregateLineOn.propTypes = {
  aggregate: PropTypes.string.isRequired,
  hasActiveEdits: PropTypes.bool.isRequired,
  setHasActiveEdits: PropTypes.func.isRequired,
};

export default withAllowedOperationsProvider(
  AggregateLineOn,
  false,
  RESOURCE_TYPE.AGGREGATE,
);

function processVcAsnResponseForMaterialGroup(asnResponse, materialGroupName) {
  const processedResponse = asnResponse.data
    .map(({ status, ...asnData }) => {
      const updatedMaterialGroupClusters = asnData.materialGroupClusters.map(
        ({ name, materials }) => ({
          name,
          materials: materials.map(material =>
            getAggregateStatusAndModifiedAtAndComment(material),
          ),
        }),
      );

      const material = updatedMaterialGroupClusters.filter(
        ({ name }) => name === materialGroupName,
      )[0]?.materials[0];

      if (!material) return null;
      return {
        ...asnData,
        id: asnData.asnId,
        vcStatus: status,
        materialGroupClusters: [...updatedMaterialGroupClusters],
        materialNumber:
          materialGroupName === AGGREGATES.MATERIAL ? null : material.material,
        materialStatus: material.aggregateStatus,
        materialDescription:
          materialGroupName === AGGREGATES.MATERIAL
            ? null
            : material.materialDescription,
        materialAvailability:
          material.aggregateStatus === AGGREGATE_STATUS.READY_FOR_PICKUP,
        materialLineoutComment: material.comment,
        modifiedAt: material.aggregateModifiedAt,
      };
    })
    .filter(i => i)
    .filter(
      ({ materialStatus }) => materialStatus === AGGREGATE_STATUS.LINE_OUT,
    )
    .map(asn => {
      const hasUnAvailableAggregates = asn.materialGroupClusters
        .filter(materialGroup => materialGroup.name !== materialGroupName)
        .map(({ materials }) => materials[0])
        .some(
          ({ aggregateStatus }) =>
            aggregateStatus === AGGREGATE_STATUS.LINE_OUT,
        );
      return { ...asn, hasUnAvailableAggregates };
    });
  processedResponse.sort((a, b) =>
    compareByDateAndSerialNumber(a, b, 'date', 'serialNumber'),
  );
  return processedResponse;
}
